/**
 * QA-only helper for assessment results empty-state testing.
 *
 * Enable via URL query params:
 *   ?qaMock=1&qaSection=<section>&qaValue=<value>
 *
 * Sections:
 *   physical, movement, sleep, mental, nutrition, hydration, productivity, energy, financial, all
 *
 * Values:
 *   null, undefined, false, empty-string, empty-object, filled
 *
 * Examples:
 *   ?qaMock=1&qaSection=movement&qaValue=null
 *   ?qaMock=1&qaSection=mental&qaValue=false
 *   ?qaMock=1&qaSection=all&qaValue=empty-object
 *   ?qaMock=1&qaSection=all&qaValue=filled
 *
 * Disable:
 *   remove params or use ?qaMock=0
 */

type SectionName =
  | 'physical'
  | 'movement'
  | 'sleep'
  | 'mental'
  | 'nutrition'
  | 'hydration'
  | 'productivity'
  | 'energy'
  | 'financial';

interface SectionConfig {
  series: string[];
  averages: string[];
  direct: string[];
}

type SectionFieldMap = Record<SectionName, SectionConfig>;

type MockValue = null | undefined | false | '' | Record<string, never>;

interface SeriesPoint {
  date: string;
  value: unknown;
}

type AssessmentScores = Record<string, unknown>;

interface ApplyQaMockOptions {
  isEnabled?: boolean;
}

const sectionFieldMap: SectionFieldMap = {
  physical: {
    series: ['smoking_data', 'bmi_data'],
    averages: ['smoking_data_average', 'bmi_data_average'],
    direct: [
      'physical_wellbeing_level',
      'smoke',
      'bmi_no_show',
      'bmi_score',
      'bmi_value',
      'bmi_colour',
      'back_pain_score',
      'back_pain_value',
      'pain',
      'msk_pain_data',
    ],
  },
  movement: {
    series: ['exercise_data', 'sitting_data'],
    averages: ['exercise_data_average', 'sitting_data_average'],
    direct: [],
  },
  sleep: {
    series: ['hours_sleep_data', 'quality_of_sleep_data', 'clock_bed_data', 'clock_wake_data'],
    averages: [
      'hours_sleep_data_average',
      'quality_of_sleep_data_average',
      'clock_bed_data_average',
      'clock_wake_data_average',
    ],
    direct: [],
  },
  mental: {
    series: ['mood_rating_data', 'stress_rating_data'],
    averages: ['mood_rating_data_average', 'stress_rating_data_average'],
    direct: [
      'anxiety_level',
      'anxiety_colour',
      'depression_level',
      'depression_colour',
      'gad_score',
      'phq_score',
    ],
  },
  nutrition: {
    series: [
      'first_meal_data',
      'last_meal_data',
      'hours_between_meals_data',
      'vegetables_data',
      'takeaways_data',
    ],
    averages: [
      'first_meal_data_average',
      'last_meal_data_average',
      'hours_between_meals_data_average',
      'vegetables_data_average',
      'takeaways_data_average',
    ],
    direct: [],
  },
  hydration: {
    series: ['water_data', 'alcohol_data'],
    averages: ['water_data_average', 'alcohol_data_average'],
    direct: ['caffeine', 'caffeine_in_bloodstream'],
  },
  productivity: {
    series: ['productivity_score_data', 'hours_worked_data'],
    averages: ['productivity_score_data_average', 'hours_worked_data_average'],
    direct: [],
  },
  energy: {
    series: ['energy_level_data', 'most_energetic_data', 'least_energetic_data'],
    averages: [
      'energy_level_data_average',
      'most_energetic_data_average',
      'least_energetic_data_average',
    ],
    direct: [],
  },
  financial: {
    series: [],
    averages: [],
    direct: [
      'financial_wellbeing_score',
      'financial_position_score',
      'confident_managing_finance',
      'financial_security',
      'cannot_meet_financial_goals',
    ],
  },
};

const allSections = Object.keys(sectionFieldMap) as SectionName[];

const parseToken = (token: string): MockValue => {
  switch (token) {
    case 'undefined':
      return undefined;
    case 'false':
      return false;
    case 'empty-string':
      return '';
    case 'empty-object':
      return {};
    case 'null':
    default:
      return null;
  }
};

const filledSeriesValueMap: Record<string, number> = {
  smoking_data: 4,
  bmi_data: 23.5,
  exercise_data: 4,
  sitting_data: 6,
  hours_sleep_data: 7.5,
  quality_of_sleep_data: 4,
  clock_bed_data: 22.5,
  clock_wake_data: 6.5,
  mood_rating_data: 4,
  stress_rating_data: 2,
  first_meal_data: 8.5,
  last_meal_data: 19.5,
  hours_between_meals_data: 11,
  vegetables_data: 5,
  takeaways_data: 1,
  water_data: 8,
  alcohol_data: 1,
  productivity_score_data: 4,
  hours_worked_data: 8,
  energy_level_data: 4,
  most_energetic_data: 10,
  least_energetic_data: 16,
};

const filledAverageValueMap: Record<string, number> = {
  smoking_data_average: 4,
  bmi_data_average: 23.5,
  exercise_data_average: 4,
  sitting_data_average: 6,
  hours_sleep_data_average: 7.5,
  quality_of_sleep_data_average: 4,
  clock_bed_data_average: 22.5,
  clock_wake_data_average: 6.5,
  mood_rating_data_average: 4,
  stress_rating_data_average: 2,
  first_meal_data_average: 8.5,
  last_meal_data_average: 19.5,
  hours_between_meals_data_average: 11,
  vegetables_data_average: 5,
  takeaways_data_average: 1,
  water_data_average: 8,
  alcohol_data_average: 1,
  productivity_score_data_average: 4,
  hours_worked_data_average: 8,
  energy_level_data_average: 4,
  most_energetic_data_average: 10,
  least_energetic_data_average: 16,
};

const filledDirectValueMap: Record<string, unknown> = {
  physical_wellbeing_level: 'moderate',
  smoke: 'yes',
  bmi_no_show: false,
  bmi_score: 24,
  bmi_value: 'healthy',
  bmi_colour: 'healthy',
  back_pain_score: 2,
  back_pain_value: 'low',
  pain: true,
  msk_pain_data: [
    {
      date: '01 Apr',
      value: {
        primary_pain_location: 'lower-back',
        secondary_locations: ['neck', 'shoulder'],
        severity: 2,
        specialist_support: 'no',
        activity_status: 'Active',
      },
    },
  ],
  anxiety_level: 'None',
  anxiety_colour: 'green',
  depression_level: 'None',
  depression_colour: 'green',
  gad_score: 2,
  phq_score: 2,
  caffeine: '1',
  caffeine_in_bloodstream: 30,
  financial_wellbeing_score: 78,
  financial_position_score: 4,
  confident_managing_finance: 4,
  financial_security: 4,
  cannot_meet_financial_goals: 2,
};

const filledTokens = new Set(['filled', 'valid', 'all-data']);

const getFilledSeriesValue = (field: string): number => {
  return filledSeriesValueMap[field] ?? 1;
};

const getFilledAverageValue = (field: string): number => {
  return filledAverageValueMap[field] ?? 1;
};

const getFilledDirectValue = (field: string): unknown => {
  return filledDirectValueMap[field] ?? 1;
};

const applyValueToSeries = (
  targetScores: AssessmentScores,
  fields: string[],
  value: MockValue
): void => {
  const mutableScores = targetScores;
  fields.forEach((field) => {
    const current = mutableScores[field];
    if (Array.isArray(current) && current.length > 0) {
      mutableScores[field] = current.map((point: SeriesPoint) => ({ ...point, value }));
      return;
    }
    mutableScores[field] = [{ date: '01 Apr', value }];
  });
};

const applyFilledToSeries = (targetScores: AssessmentScores, fields: string[]): void => {
  const mutableScores = targetScores;
  fields.forEach((field) => {
    const filledValue = getFilledSeriesValue(field);
    const current = mutableScores[field];
    if (Array.isArray(current) && current.length > 0) {
      mutableScores[field] = current.map((point: SeriesPoint) => ({
        ...point,
        value: filledValue,
      }));
      return;
    }
    mutableScores[field] = [{ date: '01 Apr', value: filledValue }];
  });
};

const applyValueToFields = (
  targetScores: AssessmentScores,
  fields: string[],
  value: MockValue
): void => {
  const mutableScores = targetScores;
  fields.forEach((field) => {
    mutableScores[field] = value;
  });
};

const applyFilledToFields = (
  targetScores: AssessmentScores,
  fields: string[],
  type: 'average' | 'direct'
): void => {
  const mutableScores = targetScores;
  fields.forEach((field) => {
    if (type === 'average') {
      mutableScores[field] = getFilledAverageValue(field);
      return;
    }
    mutableScores[field] = getFilledDirectValue(field);
  });
};

const parseSections = (rawSection: string | null): SectionName[] => {
  if (!rawSection || rawSection === 'all') {
    return allSections;
  }
  return rawSection
    .split(',')
    .map((item) => item.trim())
    .filter((item): item is SectionName => item in sectionFieldMap);
};

const applyAssessmentQaMock = (
  scores: AssessmentScores | null,
  search: string,
  options: ApplyQaMockOptions = {}
): AssessmentScores | null => {
  if (!scores) return scores;
  const { isEnabled = true } = options;
  if (!isEnabled) return scores;
  const params = new URLSearchParams(search);
  const mockEnabled = params.get('qaMock') === '1';
  if (!mockEnabled) return scores;

  const valueToken = (params.get('qaValue') || 'null').toLowerCase();
  const sectionToken = params.get('qaSection') || 'all';
  const targetSections = parseSections(sectionToken);
  const useFilledValues = filledTokens.has(valueToken);

  if (targetSections.length === 0) return scores;

  const nextScores = { ...scores };
  targetSections.forEach((section) => {
    const config = sectionFieldMap[section];
    if (useFilledValues) {
      applyFilledToSeries(nextScores, config.series);
      applyFilledToFields(nextScores, config.averages, 'average');
      applyFilledToFields(nextScores, config.direct, 'direct');
      return;
    }
    const mockValue = parseToken(valueToken);
    applyValueToSeries(nextScores, config.series, mockValue);
    applyValueToFields(nextScores, config.averages, mockValue);
    applyValueToFields(nextScores, config.direct, mockValue);
  });

  return nextScores;
};

export { applyAssessmentQaMock };
